import { useCallback, useState } from 'react';
import {
  Calendar,
  type EventProps,
  SlotInfo,
  type ToolbarProps,
  type View,
  Views,
  dateFnsLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { ButtonAction } from 'components/button/ButtonAction';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { Button, Select, Tooltip, Typography, closeModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { getAssessmentsInCourseColumn } from 'modules/dashboard/core/columns';
import { DropdownContent } from 'modules/dashboard/styles/dashboard';

import { CalendarWrapper } from '../styles/calendar';

import { ReactComponent as NextIcon } from 'assets/icons/next-icon.svg';
import { ReactComponent as PrevIcon } from 'assets/icons/prev.svg';

import type { EventCalendar } from '../core/type';

import { differenceInMinutes, format, getDay, parse, startOfWeek } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { isArray } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import ButtonAddComp from '../components/ButtonAddComp';
import { LessonInfoComp } from '../components/LessonInfoComp';
import { LessonInfoModal } from '../components/LessonInfoModal';
import { getLessonsInClassColumn } from '../core/columns';
import { useClassCalendarController } from '../core/controller/classes-calendar';
import { LessonAssessmentType } from '../core/enum';

const locales = {
  'en-US': enUS,
};

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const legendData = [
  {
    color: '#5D65D9',
    label: 'Supervised lesson',
    type: 'Supervised',
    colorSidebar: '#383ECD',
    backgroundColorSidebar: '#D6DEFF',
  },
  {
    color: '#08B3B3',
    label: 'DIY lesson',
    type: 'DIY',
    colorSidebar: '#006D75',
    backgroundColorSidebar: '#CCF0EA',
  },
  {
    color: '#EB2F96',
    label: 'Assessment',
    type: 'assessment',
    colorSidebar: '#EB2F96',
    backgroundColorSidebar: '#FFE0ED',
  },
];

export const CustomToolBar = (toolBar: ToolbarProps) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <PrevIcon onClick={() => toolBar.onNavigate('PREV')} style={{ cursor: 'pointer' }} />
        <Typography variant="h3" weight={700} style={{ margin: '0 8px' }}>
          {toolBar.label}
        </Typography>
        <NextIcon onClick={() => toolBar.onNavigate('NEXT')} style={{ cursor: 'pointer' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Select
          options={(toolBar.views as View[])
            .filter((ele) => ele !== 'agenda')
            .map((el) => ({
              key: el,
              label: el.charAt(0).toUpperCase() + el.slice(1),
            }))}
          selectedKeys={toolBar.view}
          onChange={(value) => {
            if (isArray(value) || !value) {
              return;
            }

            const newValue = value.key as View;

            toolBar.onView(newValue);
          }}
          inputStyle={{
            width: 'auto',
            minHeight: 38,
            borderRadius: 12,
          }}
        />
        <Button
          onClick={() => toolBar.onNavigate('TODAY')}
          style={{
            borderRadius: 12,
            fontWeight: 400,
            background: COLOR_CONFIG.white,
          }}
        >
          Today
        </Button>
      </div>
    </div>
  );
};

export const CustomEvent = (event: EventProps, view: View) => {
  const isViewMonth = view === 'month';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: isViewMonth ? 'row' : 'column',
      }}
    >
      <Typography
        style={
          !isViewMonth
            ? {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
                fontSize: 14,
              }
            : {}
        }
      >
        {event.title}
      </Typography>
      {!isViewMonth && (
        <Typography style={{ fontSize: 12, marginRight: 4, fontWeight: 400, width: '100%' }}>
          {format(String(event.event.start), 'h:mm a')} -{' '}
          {format(String(event.event.end), 'h:mm a')}
        </Typography>
      )}
    </div>
  );
};

export const DragAndDropCalendar = withDragAndDrop(Calendar);

export const CalendarPage = () => {
  const {
    myEvents,
    onDropFromOutside,
    lessonsData,
    handleDragStart,
    moveEvent,
    onSelectEvent,
    onChangeSelectedLessons,
    selectedLessons,
    handleAddLesson,
    tooltipVisibility,
    handleTooltipToggle,
    goToLessonDetail,
    handleDuplicateLesson,
    handleClickDeleteLesson,
    handleDeleteLesson,
    draggedEvent,
    handleChangeValue,
    handleUpdateEvent,
    onChangeSelectedAssessments,
    selectedAssessments,
    handleAddAssessment,
    institutionId,
  } = useClassCalendarController();

  const lessonColumns = getLessonsInClassColumn(onChangeSelectedLessons, selectedLessons);

  const [view, setView] = useState<View>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());

  const disabledSubmitButton =
    differenceInMinutes(draggedEvent?.start ?? '', draggedEvent?.end ?? '') >= 0 ||
    isNaN(differenceInMinutes(draggedEvent?.start ?? '', draggedEvent?.end ?? ''));

  const lessons = lessonsData.filter((el) => el.lessonType !== LessonAssessmentType.ASSESSMENTS);

  const assessments = lessonsData.filter(
    (el) => el.lessonType === LessonAssessmentType.ASSESSMENTS,
  );

  const assessmentColumns = getAssessmentsInCourseColumn(
    onChangeSelectedAssessments,
    selectedAssessments,
  );

  const eventStyleGetter = (event: EventCalendar) => ({
    ...(event.type === 'Supervised' && { className: 'text-supervised' }),
    ...(event.type === 'DIY' && { className: 'text-diy' }),
    ...(event.lessonType === LessonAssessmentType.ASSESSMENTS && { className: 'text-assessment' }),
    style: {
      backgroundColor:
        (event.type === 'Supervised' && '#D6DEFF') ||
        (event.type === 'DIY' && '#CCF0EA') ||
        (event.lessonType === LessonAssessmentType.ASSESSMENTS && '#FFE0ED'),
      border: 'none',
      borderLeft:
        '3px solid' +
        ((event.type === 'Supervised' && '#383ECD') ||
          (event.type === 'DIY' && '#006D75') ||
          (event.lessonType === LessonAssessmentType.ASSESSMENTS && '#EB2F96')),
    },
  });

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setCurrentDate(new Date(slotInfo.start));
    setView(Views.DAY);
  };

  const onNavigate = useCallback((newDate: any) => setCurrentDate(newDate), [setCurrentDate]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 24,
        position: 'relative',
      }}
    >
      <CalendarWrapper style={{ width: '75%' }}>
        <div className="legend">
          {legendData.map((el) => (
            <div key={el.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ background: el.color, width: 16, height: 16, borderRadius: 4 }}></div>
              <Typography
                style={{
                  fontSize: 16,
                  lineHeight: '22px',
                  color: COLOR.surfaceNeutralBold['neutral-11'],
                }}
              >
                {el.label}
              </Typography>
            </div>
          ))}
        </div>
        <DragAndDropCalendar
          className={
            view === 'month'
              ? ''
              : `custom-calendar-time ${view === 'day' ? 'custom-calendar-day' : ''}`
          }
          localizer={localizer}
          style={{ height: 800 }}
          date={currentDate}
          popup={false}
          doShowMoreDrillDown={false}
          events={myEvents}
          onDropFromOutside={onDropFromOutside as any}
          onEventDrop={moveEvent as any}
          onSelectEvent={onSelectEvent as any}
          formats={{
            weekdayFormat: (wd, culture, loc) => (loc ?? localizer).format(wd, 'EEE', culture),
          }}
          components={{
            toolbar: CustomToolBar,
            event: (event) => CustomEvent(event, view),
          }}
          onShowMore={(_, date) => {
            console.log('date', date);
            setCurrentDate(date);
            setView(Views.DAY);
          }}
          view={view}
          onView={setView}
          eventPropGetter={eventStyleGetter as any}
          onNavigate={onNavigate}
          onSelectSlot={handleSelectSlot}
          selectable={true}
        />
      </CalendarWrapper>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '25%',
          justifyContent: 'flex-start',
        }}
      >
        <ButtonAddComp
          lessonColumns={lessonColumns}
          handleAddLesson={handleAddLesson}
          handleAddAssessment={handleAddAssessment}
          assessmentColumns={assessmentColumns}
          institutionId={institutionId}
          style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}
        />

        <div style={{ height: 800 - 38 - 12 }}>
          <div
            style={{
              padding: '12px 16px',
              height: '100%',
              width: '100%',
              backgroundColor: '#F7F4ED',
              borderRadius: 16,
              cursor: 'pointer',
              overflow: 'auto',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Typography style={{ fontSize: 18, lineHeight: '26px' }} weight={600}>
                  Unscheduled lessons
                </Typography>
              </div>
              {lessons.map((el, index) => {
                const legend = legendData.find((lg) => lg.type === el.type);

                return (
                  <Tooltip
                    content={
                      <DropdownContent>
                        <LessonInfoComp
                          goToDetailPage={() =>
                            goToLessonDetail(el.lessonType as LessonAssessmentType)
                          }
                          handleDuplicate={handleDuplicateLesson}
                          handleDelete={handleClickDeleteLesson}
                          value={draggedEvent as EventCalendar}
                          handleChangeValue={handleChangeValue}
                        />
                        <ButtonAction
                          disabledCancel={false}
                          disabledSubmit={disabledSubmitButton}
                          titleCancel="Cancel"
                          titleSubmit="Submit"
                          style={{ justifyContent: 'end' }}
                          onSubmit={handleUpdateEvent}
                          onCancel={() => handleTooltipToggle(String(el.classLessonId), el)}
                        />
                      </DropdownContent>
                    }
                    placement="bottom"
                    visible={tooltipVisibility[el.classLessonId ?? ''] ?? false}
                    onClickOutside={() => handleTooltipToggle(String(el.classLessonId), el)}
                    bg="transparent"
                    arrow={false}
                    offset={[5, 0]}
                    allowHTML
                    interactive
                    key={index}
                    maxWidth={'auto'}
                  >
                    <div
                      style={{
                        borderRadius: 4,
                        backgroundColor: legend?.backgroundColorSidebar,
                        padding: '6px',
                        marginTop: 6,
                        borderLeft: `3px solid ${legend?.colorSidebar}`,
                      }}
                      onClick={() => handleTooltipToggle(String(el.classLessonId), el)}
                      draggable="true"
                      onDragStart={() => handleDragStart(el)}
                    >
                      <Typography
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        weight={600}
                        color={legend?.colorSidebar}
                      >
                        {el.title}
                      </Typography>
                    </div>
                  </Tooltip>
                );
              })}
              {/* <div style={{ marginTop: 12 }}>
                  <AddItemModal
                    title={'Add'}
                    options={lessonData}
                    onAdd={handleAddLesson}
                    placeholderSearch="Search by lesson"
                    searchValue={searchLesson.value}
                    onChange={onChangeSearchLesson}
                    content={
                      <Table
                        columns={lessonColumns}
                        dataSource={lessonData}
                        total={99999}
                        tableKey={''}
                        queryState={{
                          page: 1,
                          limit: 99999,
                        }}
                        paginationConfigs={{
                          show: false,
                        }}
                        headerConfigs={{
                          show: false,
                        }}
                      />
                    }
                  />
                </div> */}
            </div>
            <div>
              {/* <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Typography weight={600}>Unscheduled assessment</Typography>
                  </div> */}

              {assessments.map((el, index) => {
                const legend = legendData.find((lg) => lg.type === el.lessonType);

                return (
                  <Tooltip
                    content={
                      <DropdownContent>
                        <LessonInfoComp
                          goToDetailPage={() =>
                            goToLessonDetail(el.lessonType as LessonAssessmentType)
                          }
                          handleDuplicate={handleDuplicateLesson}
                          handleDelete={handleClickDeleteLesson}
                          value={draggedEvent as EventCalendar}
                          handleChangeValue={handleChangeValue}
                        />
                        <ButtonAction
                          disabledCancel={false}
                          disabledSubmit={disabledSubmitButton}
                          titleCancel="Cancel"
                          titleSubmit="Submit"
                          style={{ justifyContent: 'end' }}
                          onSubmit={handleUpdateEvent}
                          onCancel={() => handleTooltipToggle(String(el.classLessonId), el)}
                        />
                      </DropdownContent>
                    }
                    placement="bottom"
                    visible={tooltipVisibility[el.classLessonId ?? ''] ?? false}
                    onClickOutside={() => handleTooltipToggle(String(el.classLessonId), el)}
                    bg="transparent"
                    arrow={false}
                    offset={[5, 0]}
                    allowHTML
                    interactive
                    key={index}
                    maxWidth={'auto'}
                  >
                    <div
                      style={{
                        borderRadius: 4,
                        backgroundColor: legend?.backgroundColorSidebar,
                        padding: '6px',
                        marginTop: 6,
                        borderLeft: `3px solid ${legend?.colorSidebar}`,
                      }}
                      onClick={() => handleTooltipToggle(String(el.classLessonId), el)}
                      draggable="true"
                      onDragStart={() => handleDragStart(el)}
                    >
                      <Typography
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        weight={600}
                        color={legend?.colorSidebar}
                      >
                        {el.title}
                      </Typography>
                    </div>
                  </Tooltip>
                );
              })}
              {/* <div style={{ marginTop: 12 }}>
                  <AddItemModal
                    title={'Add'}
                    options={assessmentData}
                    onAdd={handleAddAssessment}
                    placeholderSearch="Search by assessment"
                    searchValue={searchAssessment.value}
                    onChange={onChangeSearchAssessment}
                    content={
                      <Table
                        columns={assessmentColumns}
                        dataSource={assessmentData}
                        total={99999}
                        tableKey={''}
                        queryState={{
                          page: 1,
                          limit: 99999,
                        }}
                        paginationConfigs={{
                          show: false,
                        }}
                        headerConfigs={{
                          show: false,
                        }}
                      />
                    }
                  />
                </div> */}
            </div>
          </div>
        </div>
      </div>

      <LessonInfoModal
        goToDetailPage={() => goToLessonDetail(draggedEvent?.lessonType as LessonAssessmentType)}
        handleDuplicate={handleDuplicateLesson}
        handleDelete={handleClickDeleteLesson}
        value={draggedEvent as EventCalendar}
        handleUpdateEvent={handleUpdateEvent}
        handleChangeValue={handleChangeValue}
      />
      <ConfirmModal
        modalKey={MODAL_KEY.DELETE_LESSON_IN_CLASS}
        content={
          'Doing this will also delete all results of clients completed the lesson. There’s no way to undo this.'
        }
        header={'Delete lesson'}
        onCancel={() => closeModal(MODAL_KEY.DELETE_LESSON_IN_CLASS)}
        onSubmit={handleDeleteLesson}
      />
    </div>
  );
};
